import React, { useState, useEffect } from 'react';
import { taskDataService } from '../../../services/taskDataService';
import {
  Box,
  Typography,
  Paper,
  styled,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { format, parseISO, isAfter } from 'date-fns';
import { tr } from 'date-fns/locale';

// İkonlar
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import CloseIcon from '@mui/icons-material/Close';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventIcon from '@mui/icons-material/Event';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';

// Stil tanımlamaları
const TaskContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    transition: 'box-shadow 0.3s ease'
  }
}));

const TaskCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  }
}));

const TaskCardContent = styled(CardContent)({
  flexGrow: 1,
});

const StatusChip = styled(Chip)<{ customcolor?: string }>(({ theme, customcolor }) => ({
  fontWeight: 'bold',
  backgroundColor: customcolor ? `${customcolor}20` : undefined,
  color: customcolor,
  borderColor: customcolor ? `${customcolor}50` : undefined,
  '& .MuiChip-icon': {
    color: 'inherit'
  }
}));

const ViewToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(2),
}));

const MonthBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
}));

// Ay isimleri yardımcı fonksiyonu
const getMonthName = (monthNumber: number): string => {
  const months = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];
  return months[monthNumber] || 'Bilinmeyen Ay';
};

interface TaskDay {
  repetitionTimes: string[];
}

interface TaskMonth {
  [key: string]: TaskDay; // 'day1', 'day2' gibi anahtarlar
}

interface Task {
  id: string;
  name: string;
  description: string;
  status: string;
  completionType: string;
  createdAt: number;
  startTolerance?: number;
  months: {
    [key: string]: TaskMonth; // 'month0', 'month1' gibi anahtarlar
  };
}

interface MonthWithDays {
  monthNumber: number;
  monthName: string;
  days: {
    dayNumber: number;
    repetitionTimes: string[];
  }[];
}

const MonthlyTasksComponent: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // ✅ userDetails AuthContext cache'inden - Firebase sorgusu YOK!
  const { currentUser, userDetails } = useAuth();
  
  // Bildirim için state
  const [notification, setNotification] = useState<{message: string, isError: boolean} | null>(null);

  // Modal için state
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Bildirim gösterme fonksiyonu
  const showNotification = (message: string, isError: boolean = false) => {
    setNotification({ message, isError });
    // 3 saniye sonra bildirimi kaldır
    setTimeout(() => {
      setNotification(null);
    }, isError ? 4000 : 2000);
  };

  // LocalStorage'dan kullanıcı görünüm tercihini yükle
  useEffect(() => {
    const savedViewMode = localStorage.getItem('personnelMonthlyTaskViewMode');
    if (savedViewMode && (savedViewMode === 'card' || savedViewMode === 'list')) {
      setViewMode(savedViewMode as 'card' | 'list');
    }
  }, []);

  // Görünüm modu değişikliği
  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewMode: 'card' | 'list' | null
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      // LocalStorage'a kullanıcı tercihini kaydet
      localStorage.setItem('personnelMonthlyTaskViewMode', newViewMode);
    }
  };

  // Sayfa değişikliği
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişikliği
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Görev durumuna göre renk döndürme
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return '#FFA000'; // Turuncu
      case 'accepted':
        return '#1976D2'; // Mavi
      case 'started':
        return '#43A047'; // Yeşil
      case 'completed':
        return '#6A1B9A'; // Mor
      default:
        return '#757575'; // Gri
    }
  };

  // Görev durumuna göre ikon döndürme
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <PendingIcon />;
      case 'accepted':
        return <TaskAltIcon />;
      case 'started':
        return <PlayCircleFilledIcon />;
      case 'completed':
        return <CheckCircleIcon />;
      default:
        return <ErrorOutlineIcon />;
    }
  };

  // Görev durumuna göre etiket döndürme
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'accepted':
        return 'Kabul Edildi';
      case 'started':
        return 'Başlatıldı';
      case 'completed':
        return 'Tamamlandı';
      default:
        return 'Bilinmiyor';
    }
  };

  // Firebase'den görevleri yükleme (TEK SEFERLİK)
  useEffect(() => {
    const loadTasks = async () => {
      if (!currentUser) {
        setError('Kullanıcı oturumu bulunamadı');
        setLoading(false);
        return;
      }

      // ✅ companyId AuthContext cache'inden - Firebase sorgusu YOK!
      const companyId = userDetails?.companyId;
      
      if (!companyId) {
        setError('Kullanıcı bir şirkete ait değil');
        setLoading(false);
        return;
      }

      try {
        const toMs = (iso?: string | null): number => (iso ? new Date(iso).getTime() : Date.now());
        const parseArr = (raw?: string | null): string[] => {
          if (!raw) return [];
          try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
        };

        // Kullanıcıya atanmış 'monthly' görevler — SQL (taskDataService)
        const all: any[] = [];
        let pg = 1;
        for (;;) {
          const res = await taskDataService.listTasks({
            taskType: 'monthly',
            personnelId: currentUser.uid,
            page: pg,
            pageSize: 200,
          });
          all.push(...res.items);
          if (res.items.length === 0 || all.length >= (res.totalCount || 0)) break;
          pg++;
        }

        // Slotlardan ay-gün-saat yapısını (months) kur
        const slots = await taskDataService.listSlots('monthly');
        const monthsByTask: { [taskId: number]: { [key: string]: TaskMonth } } = {};
        slots.forEach((s) => {
          if (s.monthNumber == null || s.dayOfMonth == null) return;
          if (!monthsByTask[s.taskId]) monthsByTask[s.taskId] = {};
          const mKey = `month${s.monthNumber}`;
          if (!monthsByTask[s.taskId][mKey]) monthsByTask[s.taskId][mKey] = {};
          monthsByTask[s.taskId][mKey][`day${s.dayOfMonth}`] = { repetitionTimes: parseArr(s.repetitionTimes) };
        });

        const tasksList: Task[] = all.map((tk) => ({
          id: String(tk.id),
          name: tk.name || 'İsimsiz Görev',
          description: tk.description || '',
          status: tk.status || 'pending',
          completionType: tk.completionType || 'button',
          createdAt: toMs(tk.createdAt),
          startTolerance: tk.startTolerance || 15,
          months: monthsByTask[tk.id] || {},
        } as Task));

        tasksList.sort((a, b) => b.createdAt - a.createdAt);
        setTasks(tasksList);
      } catch (error) {
        console.error('Görevler yüklenirken hata:', error);
        setError('Görevler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    loadTasks();
  }, [currentUser, userDetails?.companyId]);

  // Görev kabul etme işlemi — SQL (changeTaskStatus)
  const handleAcceptTask = async (taskId: string) => {
    if (!currentUser) return;

    try {
      await taskDataService.changeTaskStatus(Number(taskId), {
        action: 'accept',
        byUid: currentUser.uid,
      });

      showNotification('Görev başarıyla kabul edildi', false);
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId ? { ...task, status: 'accepted' } : task
        )
      );
    } catch (error) {
      console.error('Görev kabul edilirken hata:', error);
      showNotification('Görev kabul edilirken bir hata oluştu', true);
    }
  };

  // Tarih formatı
  const formatDate = (dateString: string | number): string => {
    const date = typeof dateString === 'number' 
      ? new Date(dateString) 
      : parseISO(dateString);
      
    return format(date, 'dd MMMM yyyy HH:mm', { locale: tr });
  };

  // Görev ayrıntıları modalını açma fonksiyonu
  const handleOpenDetailsModal = (task: Task) => {
    setSelectedTask(task);
    setIsDetailsModalOpen(true);
  };

  // Görev ayrıntıları modalını kapatma fonksiyonu
  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedTask(null);
  };

  // Görevin o andaki saati için durumunu kontrol et
  const getTaskTimeStatus = (task: Task, monthNumber: number, dayNumber: number, timeString: string) => {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-11
    const currentDay = now.getDate(); // 1-31
    const today = now.toISOString().split('T')[0];
    
    // Sadece bu ayın ve bugünün görevleri için zaman kontrolü yap
    if (currentMonth !== monthNumber || currentDay !== dayNumber) {
      return 'not-today'; // Bugün değil
    }
    
    // Tolerans süresi (varsayılan 15 dakika)
    const tolerance = (task.startTolerance || 15) * 60 * 1000; // milisaniye
    const taskTime = new Date(`${today}T${timeString}:00`);
    const timeDiff = now.getTime() - taskTime.getTime();
    
    // Aylık görevlerde her görev 1 saat sonraya kadar tamamlanabilir
    const completionDeadline = new Date(taskTime.getTime() + (60 * 60 * 1000)); // 1 saat ekle
    
    if (timeDiff < -tolerance) {
      return 'not-ready'; // Henüz zamanı gelmedi (tolerans öncesi)
    } else if (timeDiff >= -tolerance && now < completionDeadline) {
      return 'ready'; // Başlatılabilir ve tamamlanabilir
    } else {
      return 'missed'; // Süre geçti
    }
  };

  // Ay ve günleri işleme
  const processMonthsAndDays = (task: Task): MonthWithDays[] => {
    const processedMonths: MonthWithDays[] = [];
    
    if (task.months) {
      // Ay bilgilerini işle
      Object.keys(task.months).forEach(monthKey => {
        if (monthKey.startsWith('month')) {
          const monthNumber = parseInt(monthKey.replace('month', ''), 10);
          const monthName = getMonthName(monthNumber);
          const days: { dayNumber: number; repetitionTimes: string[] }[] = [];
          
          // Gün bilgilerini işle
          const monthData = task.months[monthKey];
          Object.keys(monthData).forEach(dayKey => {
            if (dayKey.startsWith('day')) {
              const dayNumber = parseInt(dayKey.replace('day', ''), 10);
              const dayData = monthData[dayKey];
              const repetitionTimes = dayData.repetitionTimes || [];
              
              days.push({
                dayNumber,
                repetitionTimes: Array.isArray(repetitionTimes) ? repetitionTimes : [repetitionTimes]
              });
            }
          });
          
          // Günleri sırala
          days.sort((a, b) => a.dayNumber - b.dayNumber);
          
          processedMonths.push({
            monthNumber,
            monthName,
            days
          });
        }
      });
      
      // Ayları sırala
      processedMonths.sort((a, b) => a.monthNumber - b.monthNumber);
    }
    
    return processedMonths;
  };

  // Görevin tamamlanıp tamamlanmadığını kontrol et
  const [timeSlotStatuses, setTimeSlotStatuses] = useState<{ [taskId: string]: { [month: number]: { [day: number]: { [time: string]: 'started' | 'completed' } } } }>({});

  // Tamamlanan görev zamanlarını yükle
  useEffect(() => {
    if (!currentUser || tasks.length === 0) return;

    // ✅ companyId AuthContext cache'inden - Firebase sorgusu YOK!
    const companyId = userDetails?.companyId;
    if (!companyId) return;

    const loadTimeSlotStatuses = async () => {
      try {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth();
        const day = today.getDate();
        const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

        const newStatuses: { [taskId: string]: { [month: number]: { [day: number]: { [time: string]: 'started' | 'completed' } } } } = {};

        for (const task of tasks) {
          // SQL'den bugünkü tamamlama kayıtlarını çek (taskDataService)
          const completions = await taskDataService.getCompletions(Number(task.id), dateKey);
          const taskMonthStatuses: { [month: number]: { [day: number]: { [time: string]: 'started' | 'completed' } } } = {};
          completions.forEach((c) => {
            if ((c.status === 'started' || c.status === 'completed') && c.taskTime) {
              if (!taskMonthStatuses[month]) taskMonthStatuses[month] = {};
              if (!taskMonthStatuses[month][day]) taskMonthStatuses[month][day] = {};
              taskMonthStatuses[month][day][c.taskTime] = c.status;
            }
          });
          newStatuses[task.id] = taskMonthStatuses;
        }

        setTimeSlotStatuses(newStatuses);
      } catch (error) {
        console.error('Tamamlanan görev zamanları yüklenirken hata:', error);
      }
    };

    loadTimeSlotStatuses();
  }, [currentUser, tasks, userDetails?.companyId]);

  const getNextActionForTask = (task: Task): { action: 'start' | 'complete' | 'details', month?: number, day?: number, time?: string } => {
    if (task.completionType !== 'button' || task.status !== 'accepted') {
      return { action: 'details' };
    }

    const processedMonths = processMonthsAndDays(task);
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();

    // Önce başlatılmış ama tamamlanmamış bir görev var mı diye kontrol et
    for (const month of processedMonths) {
      if (month.monthNumber === currentMonth) {
        for (const day of month.days) {
          if (day.dayNumber === currentDay) {
            const dayStatuses = timeSlotStatuses[task.id]?.[month.monthNumber]?.[day.dayNumber] || {};
            for (const time of day.repetitionTimes) {
              if (dayStatuses[time] === 'started') {
                const timeStatus = getTaskTimeStatus(task, month.monthNumber, day.dayNumber, time);
                if (timeStatus === 'ready') {
                  return { action: 'complete', month: month.monthNumber, day: day.dayNumber, time: time };
                }
              }
            }
          }
        }
      }
    }
    
    // Sonra başlatılabilecek sıradaki ilk görevi bul
    for (const month of processedMonths) {
      if (month.monthNumber === currentMonth) {
        for (const day of month.days) {
          if (day.dayNumber === currentDay) {
            const dayStatuses = timeSlotStatuses[task.id]?.[month.monthNumber]?.[day.dayNumber] || {};
            for (const time of day.repetitionTimes) {
              const timeStatus = getTaskTimeStatus(task, month.monthNumber, day.dayNumber, time);
              const isStartedOrCompleted = dayStatuses[time];
              if (timeStatus === 'ready' && !isStartedOrCompleted) {
                return { action: 'start', month: month.monthNumber, day: day.dayNumber, time: time };
              }
            }
          }
        }
      }
    }

    return { action: 'details' };
  };

  const getCurrentLocation = async (): Promise<{ latitude: number; longitude: number; accuracy: number; timestamp: number } | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.log('Geolocation is not supported by your browser.');
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        }),
        (error) => {
          console.log('Unable to retrieve your location:', error.message);
          resolve(null);
        },
        { timeout: 5000, enableHighAccuracy: true }
      );
    });
  };

  const getDeviceInfo = () => ({
    platform: 'web',
    userAgent: navigator.userAgent,
  });

  const handleStartRecurringTask = async (taskId: string, monthNumber: number, dayNumber: number, timeString: string) => {
    if (!currentUser) return;

    try {
      const today = new Date();
      const dateKey = today.toISOString().split('T')[0];
      const task = tasks.find(t => t.id === taskId);

      await taskDataService.startCompletion(Number(taskId), {
        taskTime: timeString,
        taskDate: dateKey,
        personnelFirebaseUid: currentUser.uid,
        personnelFirstName: userDetails?.firstName || '',
        personnelLastName: userDetails?.lastName || '',
        startTolerance: task?.startTolerance ?? null,
      });

      showNotification('Görev başarıyla başlatıldı', false);
      setTimeSlotStatuses(prev => {
        const newStatuses = { ...prev };
        if (!newStatuses[taskId]) newStatuses[taskId] = {};
        if (!newStatuses[taskId][monthNumber]) newStatuses[taskId][monthNumber] = {};
        if (!newStatuses[taskId][monthNumber][dayNumber]) newStatuses[taskId][monthNumber][dayNumber] = {};
        newStatuses[taskId][monthNumber][dayNumber][timeString] = 'started';
        return newStatuses;
      });
    } catch (error) {
      console.error('Aylık görev başlatılırken kritik hata:', error);
      showNotification('Aylık görev başlatılırken bir hata oluştu', true);
    }
  };

  const handleCompleteRecurringTask = async (taskId: string, monthNumber: number, dayNumber: number, timeString: string) => {
    if (!currentUser) return;

    try {
      const today = new Date();
      const dateKey = today.toISOString().split('T')[0];
      const task = tasks.find(t => t.id === taskId);

      const locationData = await getCurrentLocation();
      const deviceInfo = getDeviceInfo();

      // Bugün bu saat için kayıt bul, yoksa başlat
      const existing = await taskDataService.getCompletions(Number(taskId), dateKey);
      let completionId = existing.find(c => c.taskTime === timeString)?.id;

      if (!completionId) {
        const started = await taskDataService.startCompletion(Number(taskId), {
          taskTime: timeString,
          taskDate: dateKey,
          personnelFirebaseUid: currentUser.uid,
          personnelFirstName: userDetails?.firstName || '',
          personnelLastName: userDetails?.lastName || '',
          startTolerance: task?.startTolerance ?? null,
        });
        completionId = started.id;
      }

      await taskDataService.completeCompletion(completionId, {
        personnelFirebaseUid: currentUser.uid,
        personnelFirstName: userDetails?.firstName || '',
        personnelLastName: userDetails?.lastName || '',
        locationLat: locationData?.latitude ?? null,
        locationLng: locationData?.longitude ?? null,
        locationAccuracy: locationData?.accuracy ?? null,
        devicePlatform: deviceInfo.platform,
        deviceUserAgent: deviceInfo.userAgent,
      });

      showNotification('Görev başarıyla tamamlandı', false);
      setTimeSlotStatuses(prev => {
        const newStatuses = { ...prev };
        if (!newStatuses[taskId]) newStatuses[taskId] = {};
        if (!newStatuses[taskId][monthNumber]) newStatuses[taskId][monthNumber] = {};
        if (!newStatuses[taskId][monthNumber][dayNumber]) newStatuses[taskId][monthNumber][dayNumber] = {};
        newStatuses[taskId][monthNumber][dayNumber][timeString] = 'completed';
        return newStatuses;
      });
    } catch (error) {
      console.error('Aylık görev tamamlanırken kritik hata:', error);
      showNotification('Aylık görev tamamlanırken bir hata oluştu', true);
    }
  };

  // Kart görünümü
  const renderCardView = () => {
    return (
      <Grid container spacing={2}>
        {tasks.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">
                Atanmış aylık tekrarlı görev bulunmuyor.
              </Typography>
            </Paper>
          </Grid>
        ) : (
          tasks
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((task) => {
              const monthsWithDays = processMonthsAndDays(task);
              
              return (
                <Grid item xs={12} sm={6} md={4} key={task.id}>
                  <TaskCard>
                    <TaskCardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="h6" component="div" fontWeight="bold" gutterBottom>
                          {task.name}
                        </Typography>
                        <StatusChip
                          icon={getStatusIcon(task.status)}
                          label={getStatusLabel(task.status)}
                          customcolor={getStatusColor(task.status)}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        {task.description || 'Açıklama bulunmuyor.'}
                      </Typography>
                      <Box display="flex" alignItems="center" mb={1}>
                        <AccessTimeIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(task.createdAt)}
                        </Typography>
                      </Box>
                      
                      {monthsWithDays.length > 0 && (
                        <Box sx={{ mt: 2, mb: 1 }}>
                          <Typography variant="subtitle2" color="primary" fontWeight="medium">
                            Aylık Program:
                          </Typography>
                          <Box sx={{ mt: 1, maxHeight: '200px', overflow: 'auto' }}>
                            {monthsWithDays.map((month, monthIndex) => (
                              <Box 
                                key={monthIndex} 
                                sx={{ 
                                  mt: 1, 
                                  p: 1, 
                                  bgcolor: 'background.paper', 
                                  borderRadius: 1, 
                                  border: '1px solid', 
                                  borderColor: 'divider',
                                  mb: 1
                                }}
                              >
                                <Typography variant="body2" fontWeight="medium" color="primary">
                                  {month.monthName}
                                </Typography>
                                <Divider sx={{ my: 0.5 }} />
                                {month.days.map((day, dayIndex) => (
                                  <Box key={dayIndex} sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" fontWeight="medium" color="text.secondary">
                                      {day.dayNumber} {month.monthName}:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {day.repetitionTimes.map((time, timeIndex) => {
                                    const timeStatus = getTaskTimeStatus(task, month.monthNumber, day.dayNumber, time);
                                    // const isCompleted = completedTaskTimes[task.id]?.includes(time);
                                    
                                    // Buton tamamlama tipi olan görevler için bugünün saatleri için butonlar göster
                                    if (task.completionType === 'button' && task.status === 'accepted' && timeStatus === 'ready') {
                                      return (
                                        <Box key={timeIndex} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <Chip
                                          label={time}
                                          size="small"
                                          variant="outlined"
                                          sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}
                                        />
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            startIcon={<PlayArrowIcon />}
                                            onClick={() => showNotification('Aylık görevler için başlatma özelliği yakında eklenecek')}
                                            sx={{ minWidth: 'auto', px: 1 }}
                                          >
                                            Başlat
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            startIcon={<StopIcon />}
                                            onClick={() => showNotification('Aylık görevler için tamamlama özelliği yakında eklenecek')}
                                            sx={{ minWidth: 'auto', px: 1 }}
                                          >
                                            Tamamla
                                          </Button>
                                        </Box>
                                      );
                                    } else {
                                      return (
                                        <Chip
                                          key={timeIndex}
                                          label={time}
                                          size="small"
                                          variant="outlined"
                                          color={timeStatus === 'missed' ? 'error' : 
                                                 timeStatus === 'ready' ? 'primary' : 'default'}
                                          sx={{ 
                                            bgcolor: timeStatus === 'missed' ? 'rgba(244, 67, 54, 0.08)' : 
                                                    timeStatus === 'ready' ? 'rgba(25, 118, 210, 0.08)' : 
                                                    'rgba(158, 158, 158, 0.08)'
                                          }}
                                        />
                                      );
                                    }
                                  })}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </TaskCardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                      {renderTaskActions(task)}
                    </CardActions>
                  </TaskCard>
                </Grid>
              );
            })
        )}
      </Grid>
    );
  };

  // Tablo görünümü
  const renderTableView = () => {
    return (
      <TableContainer component={Paper} sx={{ 
        mt: 2, 
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: 2,
        overflow: 'auto',
        maxHeight: '60vh',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Görev Adı</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Açıklama</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Durum</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Oluşturulma Tarihi</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Aylık Program</TableCell>
              <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Atanmış aylık tekrarlı görev bulunmuyor.
                </TableCell>
              </TableRow>
            ) : (
              tasks
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((task) => {
                  const monthsWithDays = processMonthsAndDays(task);
                  
                  return (
                    <TableRow key={task.id}>
                      <TableCell>{task.name}</TableCell>
                      <TableCell>
                        {task.description.length > 50
                          ? `${task.description.substring(0, 50)}...`
                          : task.description || 'Açıklama bulunmuyor.'}
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          icon={getStatusIcon(task.status)}
                          label={getStatusLabel(task.status)}
                          customcolor={getStatusColor(task.status)}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(task.createdAt)}</TableCell>
                      <TableCell>
                        {monthsWithDays.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {monthsWithDays.slice(0, 3).map((month, index) => (
                              <Chip
                                key={index}
                                label={month.monthName}
                                size="small"
                                variant="outlined"
                                icon={<DateRangeIcon fontSize="small" />}
                                sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}
                              />
                            ))}
                            {monthsWithDays.length > 3 && (
                              <Chip
                                label={`+${monthsWithDays.length - 3}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {renderTaskActions(task)}
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Kart görünümü için task action butonlarını render et
  const renderTaskActions = (task: Task) => {
    // 1. Beklemedeki görevler için her zaman "Kabul Et" butonu gösterilir.
    if (task.status === 'pending') {
        return (
          <Button 
            size="small" 
            variant="contained" 
            color="primary"
          onClick={(e) => {
            e.stopPropagation();
            handleAcceptTask(task.id);
          }}
          sx={{ 
            minWidth: '80px', 
            whiteSpace: 'nowrap',
          }}
          >
            Kabul Et
          </Button>
        );
    }

    // 2. QR kodlu görevler için (beklemede olmayanlar) mobil uygulama butonu gösterilir.
    if (task.completionType === 'qr') {
        return (
          <Button
            size="small"
            variant="outlined"
            color="primary"
          startIcon={<PhoneAndroidIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDetailsModal(task);
          }}
          >
          Mobil Uygulama Gerekli
          </Button>
        );
    }

    // 3. Butonla tamamlanan ve kabul edilmiş görevler için "akıllı" buton mantığı
    const { action, month, day, time } = getNextActionForTask(task);

    switch (action) {
      case 'start':
        return (
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<PlayArrowIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleStartRecurringTask(task.id, month!, day!, time!);
            }}
          >
            Başlat ({time})
          </Button>
        );
      case 'complete':
        return (
          <Button
            size="small"
            variant="contained"
            style={{ backgroundColor: '#6A1B9A', color: 'white' }}
            startIcon={<StopIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleCompleteRecurringTask(task.id, month!, day!, time!);
            }}
          >
            Tamamla ({time})
          </Button>
        );
      case 'details':
      default:
        // Butonlu görevlerde aktif eylem yoksa hiçbir şey gösterme
        return null;
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <ViewToggleContainer>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="görünüm modu"
          size="small"
        >
          <ToggleButton value="card" aria-label="kart görünümü">
            <Tooltip title="Kart Görünümü">
              <ViewModuleIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="list" aria-label="liste görünümü">
            <Tooltip title="Liste Görünümü">
              <ViewListIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </ViewToggleContainer>

      {viewMode === 'card' ? renderCardView() : renderTableView()}

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={tasks.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Sayfa başına satır:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />

      {/* Bildirim Snackbar'ı */}
      {notification && (
        <Snackbar 
          open={true}
          autoHideDuration={notification.isError ? 4000 : 2000} 
          onClose={() => setNotification(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setNotification(null)} 
            severity={notification.isError ? "error" : "success"}
            sx={{
              fontWeight: 'bold',
              fontSize: '16px',
              width: '100%'
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}

      {/* Görev Ayrıntıları Modal'ı */}
      <Dialog
        open={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#1976D2',
          color: 'white',
          py: 2
        }}>
          <Box display="flex" alignItems="center">
            <PhoneAndroidIcon sx={{ mr: 1 }} />
            {selectedTask?.completionType === 'button' ? 'Görev Detayları' : 'Mobil Uygulama Gerekli'}
          </Box>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={handleCloseDetailsModal} 
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', color: '#1976D2' }}>
            {selectedTask?.name}
          </Typography>
          {selectedTask?.completionType === 'button' ? (
            <>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                Bu görev web üzerinden buton ile tamamlanabilir. Aylık program çerçevesinde belirlenen günlerde görev saatleri geldiğinde mobil uygulama üzerinden işlem yapabilirsiniz.
          </Typography>
          
          {selectedTask && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                Aylık Program:
              </Typography>
              {processMonthsAndDays(selectedTask).map((month, monthIndex) => (
                <MonthBox key={monthIndex}>
                  <Typography variant="body1" fontWeight="medium" color="primary" sx={{ mb: 1 }}>
                    <EventIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    {month.monthName}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  {month.days.map((day, dayIndex) => (
                    <Box key={dayIndex} sx={{ pl: 2, mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium" color="text.primary">
                        {day.dayNumber} {month.monthName}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, pl: 2 }}>
                              {day.repetitionTimes.map((time, timeIndex) => {
                                const timeStatus = getTaskTimeStatus(selectedTask, month.monthNumber, day.dayNumber, time);
                                return (
                          <Chip
                            key={timeIndex}
                            label={time}
                            size="small"
                                    color={timeStatus === 'missed' ? 'error' : 
                                           timeStatus === 'ready' ? 'primary' : 'default'}
                                    sx={{ 
                                      bgcolor: timeStatus === 'missed' ? 'rgba(244, 67, 54, 0.08)' : 
                                              timeStatus === 'ready' ? 'rgba(25, 118, 210, 0.08)' : 
                                              'rgba(158, 158, 158, 0.08)'
                                    }}
                                  />
                                );
                              })}
                            </Box>
                        </Box>
                      ))}
                    </MonthBox>
                  ))}
                </Box>
              )}
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Buton tipindeki aylık görevler şu anda mobil uygulama üzerinden tamamlanmaktadır. Web desteği yakında eklenecektir.
                </Typography>
              </Alert>
            </>
          ) : (
            <>
              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                Bu görev bir aylık tekrarlı görevdir ve sadece mobil uygulama üzerinden QR kod okutarak başlatıp bitirilebilir.
              </Typography>
              
              {selectedTask && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Aylık Program:
                  </Typography>
                  {processMonthsAndDays(selectedTask).map((month, monthIndex) => (
                    <MonthBox key={monthIndex}>
                      <Typography variant="body1" fontWeight="medium" color="primary" sx={{ mb: 1 }}>
                        <EventIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                        {month.monthName}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      {month.days.map((day, dayIndex) => (
                        <Box key={dayIndex} sx={{ pl: 2, mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium" color="text.primary">
                            {day.dayNumber} {month.monthName}
                          </Typography>
                                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, pl: 2 }}>
                          {day.repetitionTimes.map((time, timeIndex) => {
                            const timeStatus = getTaskTimeStatus(selectedTask, month.monthNumber, day.dayNumber, time);
                            return (
                              <Chip
                                key={timeIndex}
                                label={time}
                                size="small"
                                color={timeStatus === 'missed' ? 'error' : 
                                       timeStatus === 'ready' ? 'primary' : 'default'}
                                sx={{ 
                                  bgcolor: timeStatus === 'missed' ? 'rgba(244, 67, 54, 0.08)' : 
                                          timeStatus === 'ready' ? 'rgba(25, 118, 210, 0.08)' : 
                                          'rgba(158, 158, 158, 0.08)'
                                }}
                          />
                            );
                          })}
                      </Box>
                    </Box>
                  ))}
                </MonthBox>
              ))}
            </Box>
          )}
          
          <Typography variant="body1" paragraph>
            Lütfen mobil uygulamalarımızı indiriniz:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 3, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<AndroidIcon />}
              onClick={() => window.open('https://play.google.com/store/apps/details?id=com.mt_teknoloji.gts', '_blank')}
              sx={{ 
                bgcolor: '#1976D2', 
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 'bold',
                textTransform: 'none',
              }}
            >
              Google Play
            </Button>
            <Button
              variant="contained"
              startIcon={<AppleIcon />}
              onClick={() => window.open('https://apps.apple.com/tr/app/gts-g%C3%B6rev-takip-sistemi/id6743622025?l=tr&platform=iphone', '_blank')}
              sx={{ 
                bgcolor: '#1976D2', 
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 'bold',
                textTransform: 'none',
              }}
            >
              App Store
            </Button>
          </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDetailsModal} 
            variant="outlined" 
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthlyTasksComponent; 